/**
 * Asset-checklist upload helper.
 *
 * Uploads a compressed photo captured during Check-Out / Check-In /
 * Pre-Trip flows straight to OneDrive via the backend so we never
 * persist multi-megabyte base64 blobs inside MongoDB.
 *
 * Symmetric with `fieldReportUploads.js` — same shape, same
 * unique-filename strategy, so both flows behave identically for the
 * reviewer downstream.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Upload a single (already-compressed) image against a specific asset.
 * @param {string} assetId  - the asset the photo belongs to
 * @param {File|Blob} file  - compressed image (see browser-image-compression)
 * @param {string} token    - auth bearer
 * @returns {Promise<{url: string, public_id: string, storage: string}>}
 */
export async function uploadAssetChecklistPhoto(assetId, file, token) {
  // Guarantee a unique filename per upload — mobile cameras often hand
  // us `image.jpg` for every shot, and OneDrive PUT-to-path REPLACES
  // any file with the same name in the same folder. See the same fix
  // in fieldReportUploads.js.
  const orig = file.name || 'photo.jpg';
  const dot = orig.lastIndexOf('.');
  const base = dot > 0 ? orig.slice(0, dot) : orig;
  const ext = dot > 0 ? orig.slice(dot) : '.jpg';
  const stamp = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const uniqueName = `${base}_${stamp}${ext}`;

  const fd = new FormData();
  fd.append('file', file, uniqueName);

  const res = await axios.post(
    `${API}/api/assets/${assetId}/checklist-photo`,
    fd,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return res.data;
}
